<template>
    <v-layout row>
        <v-flex xs12 sm12>
            <v-card class="elevation-1 transparent">
                <v-toolbar class="transparent" flat height="37">
                    <v-btn dark icon title="Back to previous page" @click="$router.go(-1)">
                        <v-icon>arrow_back</v-icon>
                    </v-btn>
                    <v-spacer></v-spacer>
                    <v-btn icon dark @click="exportToExcel" :loading="loading_xls" title="Export report to XLSX">
                        <v-icon>cloud_download</v-icon>
                    </v-btn>

                    <!-- Leave Types -->
                    <v-menu v-if="selectedLeaveName" left v-model="menu_balances" offset-y > 
                        <template v-slot:activator="{ on }">
                            <v-btn small outline dark v-on="on">
                                <span class="ml-7 no-uppercase">{{selectedLeaveName}}</span>
                                <v-icon v-show="!menu_balances">arrow_drop_down</v-icon>
                                <v-icon v-show="menu_balances">arrow_drop_up</v-icon>
                            </v-btn>
                        </template>
                        <v-list dense>
                            <v-list-tile v-for="(item,index) in leave_types_filtered" :key="index" @click="setSelectedLeave(item)">
                                <v-list-tile-title small>{{item.leaveName}}</v-list-tile-title>
                            </v-list-tile>
                        </v-list>
                    </v-menu>

                    <!-- Calendar Years -->
                    <v-menu left v-model="menu_status" offset-y > 
                        <template v-slot:activator="{ on }">
                            <v-btn color="primary" dark v-on="on" class="mr-0">
                                <span class="ml-7">CY {{selectedFiscalYear}}</span>
                                <v-icon v-show="!menu_status">arrow_drop_down</v-icon>
                                <v-icon v-show="menu_status">arrow_drop_up</v-icon>
                            </v-btn>
                        </template>
                        <v-list dense>
                            <v-list-tile v-for="(item,index) in fiscal_years" :key="index" @click="setSelectedYear(item.fiscalYear)" :title="`Calendar Year ${item.fiscalYear}`">
                                <v-list-tile-title small>CY {{item.fiscalYear}}</v-list-tile-title>
                            </v-list-tile>
                        </v-list>
                    </v-menu>
                </v-toolbar>
            </v-card>

            <!-- details -->
            <v-card class="elevation-1 mt-5"> 
                <v-toolbar flat height="45px">
                    <div class="wp-30 mb-10">
                        <v-text-field v-model="search" append-icon="search" :label="searchLabel" single-line hide-details clearable></v-text-field>
                    </div>
                    <v-spacer></v-spacer>
                    <b>Leave Balances In Days</b>
                </v-toolbar>
                <v-data-table
                    :headers="isMobile ? [] : headers"
                    :items="timeoffs_balances_filtered"
                    :loading="loading"
                    :rows-per-page-items="rowsPerPageItems"
                    :pagination.sync="paginationSync"
                    :hide-headers="isMobile"
                >
                    <template v-slot:items="props">

                        <!-- mobile view -->
                        <tr v-if="isMobile">
                            <td :colspan="headers.length" class="pa-1">
                                <v-card class="elevation-1">
                                    <v-toolbar flat height="42">
                                        <v-chip class="user-chip-container">
                                            <v-avatar>
                                                <v-img ref="avatar" :src="awsS3Helper.getAvatarUrl(props.item.userId)"></v-img>
                                            </v-avatar>
                                            {{props.item.firstName}} {{props.item.lastName}}
                                        </v-chip>
                                        <v-spacer></v-spacer>
                                    </v-toolbar>
                                    <v-layout row wrap align-center class="section-layout">
                                        <v-flex xs12 class="text-xs-left mt-5">
                                            <v-layout row>
                                                <v-flex xs4>
                                                    <div class="caption grey--text">Annual Credits</div>
                                                    <div>
                                                        <span v-if="isTimeInLieu(props.item)">NA</span>
                                                        <span v-else>{{ numFormat.formatNumber(props.item.annualAllocation) }}</span>
                                                    </div>
                                                </v-flex>
                                                <v-flex xs4>
                                                    <div class="caption grey--text">Pro-Rated</div>
                                                    <div>
                                                         <span v-if="isTimeInLieu(props.item)">NA</span>
                                                        <span v-else>
                                                            <span v-if="props.item.prorated">Yes</span>
                                                            <span v-else>No</span>
                                                        </span>
                                                    </div>
                                                </v-flex>
                                                <v-flex xs4>
                                                    <div class="caption grey--text">Earned</div>
                                                    <div>{{ numFormat.formatNumber(props.item.earnedDays) }}</div>
                                                </v-flex>
                                            </v-layout>
                                            <v-layout row>
                                                <v-flex xs4>
                                                    <div class="caption grey--text">Used</div>
                                                    <div>{{numFormat.formatNumber(props.item.approvedTimeOffDays + props.item.forReviewTimeOffDays)}}</div>
                                                </v-flex>
                                                <v-flex xs4>
                                                    <div class="caption grey--text">Balance</div>
                                                    <div>{{ numFormat.formatNumber(props.item.balanceDays) }}</div>
                                                </v-flex>
                                            </v-layout>
                                        </v-flex>
                                    </v-layout>
                                </v-card>
                            </td>
                        </tr>

                        <!-- desktop view -->
                        <tr v-else>
                            <td>
                                <v-chip class="user-chip-container">
                                    <v-avatar>
                                        <v-img ref="avatar" :src="awsS3Helper.getAvatarUrl(props.item.userId)"></v-img>
                                    </v-avatar>
                                    {{props.item.firstName}} {{props.item.lastName}}
                                </v-chip>
                            </td>
                            <td>
                                <span v-if="isTimeInLieu(props.item)">NA</span>
                                <span v-else>{{ numFormat.formatNumber(props.item.annualAllocation) }}</span>
                            </td>
                            <td>
                                <span v-if="isTimeInLieu(props.item)">NA</span>
                                <span v-else>
                                    <span v-if="props.item.prorated">Yes</span>
                                    <span v-else>No</span>
                                </span>
                            </td>
                            <td>
                                {{ numFormat.formatNumber(props.item.earnedDays) }}
                            </td>
                            <td>
                                {{numFormat.formatNumber(props.item.approvedTimeOffDays + props.item.forReviewTimeOffDays)}}
                            </td>
                            <td>
                                {{ numFormat.formatNumber(props.item.balanceDays) }}
                            </td>
                        </tr>
                    </template>
                </v-data-table> 
            </v-card>
        </v-flex>
    </v-layout> 
</template>

<script>
    
import timeoffApi from '@/api/timeoffs'
import leaveTypesApi from '@/api/leavetypes'
import commonfn from '@/common/functions'
import numFormat from '@/common/num_format'
import awsS3Helper from '@/helpers/aws-s3'
import rowsPerPageItems from '@/constants/rows-per-page'
import { getSort, getRowsPerPage, getSearchKeyword, getCalendarYear, updateRowsPerPage, updateSort, updateSearchKeyword, updateCalendarYear } from '@/helpers/user-prefs'

export default {     
    name: 'SettingsMenu',
    metaInfo: {
        title: 'Settings Menu',
        meta: [
            { charset: 'utf-8' },
            { name: 'description', content: 'Settings Menu' },
            { name: 'viewport', content: 'width=device-width, initial-scale=1' }
        ]
    },
    components: {},   
    data () {
        const routeName = this.$route.name
        const savedSort = getSort(routeName)

        return {
            commonfn,
            awsS3Helper,
            numFormat,
            rowsPerPageItems,
            menu_status: false,
            menu_balances: false,
            selectedFiscalYear: getCalendarYear(routeName),
            search: getSearchKeyword(routeName),
            paginationSync: {
                rowsPerPage: getRowsPerPage(routeName),
                sortBy: savedSort.sortBy,
                descending: savedSort.descending,
            },
            headers: [
                {text: 'Employee',align: 'left',value: 'lastName', width: '30%', sortable:true},
                {text: 'Annual Credits',align: 'left',value: '', width: '10%', sortable:false},
                {text: 'Pro-Rated',align: 'left',value: '', width: '10%', sortable:false},
                {text: 'Earned',align: 'left',value: '', width: '10%', sortable:false},
                {text: 'Used',align: 'left',value: '',  width: '10%', sortable:false},
                {text: 'Balance',align: 'left',value: '',  width: '10%', sortable:false},
            ],
            leaveTypeId: null,
            leave_types: [],
            timeoffs_balances: [],
            error: false,
            errorMessage: '',
            loading: false,
            loading_xls: false,
        }
    },
    methods: {
        setSelectedYear(fiscalYear) {
            this.selectedFiscalYear = fiscalYear
            updateCalendarYear(this.$route.name, fiscalYear)
            this.getTimeOffBalances(true)
        },
        setSelectedLeave(data) {
            this.leaveTypeId = data.leaveTypeId
            this.getTimeOffBalances(true)
        },
        viewDetails(data){
            this.$router.push(`/myteam/timeoff/balances/${data.userId}`)
        },
        isTimeInLieu(data){
            return data.leaveCode == 'OFFSET'
        },
        async getLeaveTypes() {
            try {
                this.loading = true
                const response = await leaveTypesApi.getByCompany()
                if (response && response.status == 200) {
                    this.leave_types = response.data.items
                    this.leaveTypeId = this.leave_types.find(x => x.leaveCode=='VL').leaveTypeId
                }
                this.loading = false
            }catch(error){
                this.error = true
                this.errorMessage = error
            }
        },
        async getTimeOffBalances(show_loading) {
            try {
                this.loading = show_loading
                const response = await timeoffApi.getBalancesSummary(this.selectedTeamId, this.leaveTypeId, this.selectedFiscalYear)
                if (response && response.status == 200) {
                    this.timeoffs_balances = response.data.items
                }
                this.loading = false
            }catch(error){
                this.error = true
                this.errorMessage = error
            }
        },
        async exportToExcel() {
            try {
                this.loading_xls = true
                const response = await timeoffApi.getBalancesSummaryExcel(this.selectedTeamId, this.leaveTypeId, this.selectedFiscalYear)
                var blob = new Blob([response.data], { type: 'application/vnd.ms-excel' })
                var link = document.createElement('a')
                link.href = window.URL.createObjectURL(blob)
                link.download =  `${this.selectedLeaveName} Balances-${this.selectedFiscalYear}-${this.selectedTeamName}.xlsx`
                link.click()
                this.loading_xls = false
            }catch(error){
                this.error = true
                this.errorMessage = error
            }
        },
        async initialize() {
            await this.getLeaveTypes()
            this.getTimeOffBalances(true)
        },
        teamChangeHandler(data) {
            if (data === 'team-changed-handler') {
                this.initialize()
            }
        }
    },
    async created() {
        this.initialize()

        //update header titles
        this.$root.$emit('setHeaderTitles', {
            display: true,
            headerTitle: '',
            headerPageTitle: 'Team Leave Balances',
            isTeamPage: true
        })

        // Listen for the change event
        this.$root.$on('team-changed', this.teamChangeHandler)
    },
    beforeDestroy() {
        this.$root.$off('team-changed', this.teamChangeHandler)
    },
    watch: {
        'paginationSync.rowsPerPage'(newVal) {
            updateRowsPerPage(this.$route.name, newVal)
        },
        'paginationSync.sortBy'(newVal) {
            updateSort(this.$route.name, newVal, this.paginationSync.descending)
        },
        'paginationSync.descending'(newVal) {
            updateSort(this.$route.name, this.paginationSync.sortBy, newVal)
        },
        search(newVal) {
            updateSearchKeyword(this.$route.name, newVal)
        }
    },
    computed: {
        fiscal_years() {
            if (this.$store.state.fiscalPeriods) {
                return this.$store.state.fiscalPeriods
            }
            return [{fiscalYear: new Date().getFullYear()}]
        },
        timeoffs_balances_filtered() {
            var temp = this.timeoffs_balances
            
            //search handler
            if (this.search) {
                const searchLower = this.search.toLowerCase()
                temp = temp.filter(item => {
                    const firstNameMatch = item.firstName.toLowerCase().includes(searchLower)
                    const lastNameMatch = item.lastName.toLowerCase().includes(searchLower)
                    return firstNameMatch || lastNameMatch
                })
            }
            return temp 
        },
        selectedLeaveName() {
            if (this.leaveTypeId)
                return this.leave_types.find(x => x.leaveTypeId == this.leaveTypeId).leaveName
        },
        leave_types_filtered() {
            return this.leave_types.filter(x => x.assignThruAllocation || x.leaveCode == 'OFFSET')
        },
        selectedTeamId() {
            return this.$store.state.selectedTeamId
        },
        selectedTeamName() {
            return this.$store.state.selectedTeamName
        },
        searchLabel() {
            return this.isMobile ? 'Search' : 'Search employee name'
        }
    }
}
</script>
<style scoped>
.section-layout {
    padding:0px 23px 0px 23px;
}
</style>